import { useCallback, useEffect, useRef } from "react";
import type { ViewType } from "../types";
import {
  buildDashboardUrl,
  parseDashboardRoute,
  type ClientDetailSection,
  type DashboardRoute,
} from "../utils/dashboardRoutes";
import {
  applyDashboardScrollAfterNavigation,
  disableBrowserScrollRestoration,
  saveScrollForRoute,
} from "../utils/dashboardScrollRestoration";

export type DashboardNavigateOptions = {
  replace?: boolean;
  /** Forward navigations default to top; close/back should restore prior scroll. */
  scroll?: "top" | "restore";
};

export type DashboardNavigationState = {
  routeClientId: string | null;
  routeSection: ClientDetailSection | null;
  navigateDashboard: (
    route: DashboardRoute,
    options?: DashboardNavigateOptions,
  ) => void;
  openClient: (
    clientId: string,
    options?: { view?: ViewType; section?: ClientDetailSection; replace?: boolean },
  ) => void;
  closeClient: (options?: { view?: ViewType }) => void;
};

type UseDashboardNavigationArgs = {
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  routeClientId: string | null;
  setRouteClientId: (id: string | null) => void;
  routeSection: ClientDetailSection | null;
  setRouteSection: (section: ClientDetailSection | null) => void;
  enabled: boolean;
};

function applyRoute(
  route: DashboardRoute,
  setCurrentView: (view: ViewType) => void,
  setRouteClientId: (id: string | null) => void,
  setRouteSection: (section: ClientDetailSection | null) => void,
) {
  setCurrentView(route.view);
  setRouteClientId(route.clientId ?? null);
  setRouteSection(route.section ?? null);
}

function routeFromParts(
  view: ViewType,
  clientId: string | null,
  section: ClientDetailSection | null,
): DashboardRoute {
  return {
    view,
    clientId: clientId ?? undefined,
    section: section ?? undefined,
  };
}

export function useDashboardNavigation({
  currentView,
  setCurrentView,
  routeClientId,
  setRouteClientId,
  routeSection,
  setRouteSection,
  enabled,
}: UseDashboardNavigationArgs): DashboardNavigationState {
  const suppressUrlSyncRef = useRef(false);
  const historyIdxRef = useRef(
    typeof window !== "undefined"
      ? ((window.history.state?.dashboardNavIdx as number | undefined) ?? 0)
      : 0,
  );

  const routeRef = useRef(routeFromParts(currentView, routeClientId, routeSection));
  useEffect(() => {
    routeRef.current = routeFromParts(currentView, routeClientId, routeSection);
  }, [currentView, routeClientId, routeSection]);

  const navigateDashboard = useCallback(
    (route: DashboardRoute, options?: DashboardNavigateOptions) => {
      saveScrollForRoute(routeRef.current);

      suppressUrlSyncRef.current = true;
      applyRoute(route, setCurrentView, setRouteClientId, setRouteSection);
      const url = buildDashboardUrl(route);
      if (options?.replace) {
        window.history.replaceState(
          { dashboardRoute: route, dashboardNavIdx: historyIdxRef.current },
          "",
          url,
        );
      } else {
        historyIdxRef.current += 1;
        window.history.pushState(
          { dashboardRoute: route, dashboardNavIdx: historyIdxRef.current },
          "",
          url,
        );
      }

      applyDashboardScrollAfterNavigation(route, options?.scroll ?? "top");

      queueMicrotask(() => {
        suppressUrlSyncRef.current = false;
      });
    },
    [setCurrentView, setRouteClientId, setRouteSection],
  );

  const openClient = useCallback(
    (
      clientId: string,
      options?: {
        view?: ViewType;
        section?: ClientDetailSection;
        replace?: boolean;
      },
    ) => {
      navigateDashboard(
        {
          view: options?.view ?? currentView,
          clientId,
          section: options?.section,
        },
        { replace: options?.replace, scroll: "top" },
      );
    },
    [currentView, navigateDashboard],
  );

  const closeClient = useCallback(
    (options?: { view?: ViewType }) => {
      navigateDashboard(
        { view: options?.view ?? currentView },
        { scroll: "restore" },
      );
    },
    [currentView, navigateDashboard],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    disableBrowserScrollRestoration();

    const syncFromLocation = () => {
      if (suppressUrlSyncRef.current) return;
      const parsed = parseDashboardRoute();
      if (!parsed) return;
      applyRoute(parsed, setCurrentView, setRouteClientId, setRouteSection);
    };

    syncFromLocation();

    const onPopState = (event: PopStateEvent) => {
      saveScrollForRoute(routeRef.current);

      const newIdx =
        (event.state?.dashboardNavIdx as number | undefined) ?? 0;
      const goingBack = newIdx < historyIdxRef.current;
      historyIdxRef.current = newIdx;

      syncFromLocation();

      const parsed = parseDashboardRoute();
      if (!parsed) return;

      applyDashboardScrollAfterNavigation(
        parsed,
        goingBack ? "restore" : "top",
      );
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enabled, setCurrentView, setRouteClientId, setRouteSection]);

  return {
    routeClientId,
    routeSection,
    navigateDashboard,
    openClient,
    closeClient,
  };
}
